import { useEffect, type ReactNode } from 'react';
import { AlertTriangle, Save, Info, X } from 'lucide-react';
import { cn } from '@/utils/cn';
import { Spinner } from '@/components/ui/Spinner';

// ── Types ──────────────────────────────────────────────────────────────────────

export type ConfirmDialogVariant = 'save' | 'delete' | 'warning' | 'info';

export interface ConfirmDialogProps {
  isOpen:        boolean;
  title:         string;
  message:       string | ReactNode;
  confirmLabel?: string;
  cancelLabel?:  string;
  variant?:      ConfirmDialogVariant;
  loading?:      boolean;
  onConfirm:     () => void;
  onCancel:      () => void;
}

// ── Variant config ─────────────────────────────────────────────────────────────

const VARIANT = {
  save: {
    icon:       <Save size={20} />,
    iconBg:     'bg-emerald-100 dark:bg-emerald-900/30',
    iconColor:  'text-emerald-600 dark:text-emerald-400',
    confirmCls: 'bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-500',
    confirmDefault: 'Yes, Save',
  },
  delete: {
    icon:       <AlertTriangle size={20} />,
    iconBg:     'bg-rose-100 dark:bg-rose-900/30',
    iconColor:  'text-rose-600 dark:text-rose-400',
    confirmCls: 'bg-rose-600 hover:bg-rose-700 focus:ring-rose-500',
    confirmDefault: 'Yes, Delete',
  },
  warning: {
    icon:       <AlertTriangle size={20} />,
    iconBg:     'bg-amber-100 dark:bg-amber-900/30',
    iconColor:  'text-amber-600 dark:text-amber-400',
    confirmCls: 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500',
    confirmDefault: 'Yes',
  },
  info: {
    icon:       <Info size={20} />,
    iconBg:     'bg-blue-100 dark:bg-blue-900/30',
    iconColor:  'text-blue-600 dark:text-blue-400',
    confirmCls: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
    confirmDefault: 'Yes',
  },
} satisfies Record<ConfirmDialogVariant, {
  icon: ReactNode;
  iconBg: string;
  iconColor: string;
  confirmCls: string;
  confirmDefault: string;
}>;

// ── Component ─────────────────────────────────────────────────────────────────

export const ConfirmDialog = ({
  isOpen,
  title,
  message,
  confirmLabel,
  cancelLabel  = 'No',
  variant      = 'info',
  loading      = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) => {
  const cfg = VARIANT[variant];

  // Keyboard: Escape → cancel
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) onCancel();
      if (e.key === 'Enter'  && !loading) onConfirm();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, loading, onCancel, onConfirm]);

  if (!isOpen) return null;

  const label = confirmLabel ?? cfg.confirmDefault;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      className="fixed inset-0 z-[500] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onMouseDown={e => { if (e.target === e.currentTarget && !loading) onCancel(); }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4 animate-in fade-in zoom-in-95 duration-150">

        {/* Header */}
        <div className="flex items-start gap-3 mb-4">
          <div className={cn(
            'flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center',
            cfg.iconBg,
          )}>
            <span className={cfg.iconColor}>{cfg.icon}</span>
          </div>
          <div className="flex-1 min-w-0 pt-0.5">
            <h3
              id="confirm-dialog-title"
              className="text-sm font-semibold text-gray-900 dark:text-gray-100"
            >
              {title}
            </h3>
          </div>
          {!loading && (
            <button
              type="button"
              onClick={onCancel}
              className="flex-shrink-0 p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label="Close"
            >
              <X size={15} />
            </button>
          )}
        </div>

        {/* Message */}
        <div className="text-sm text-gray-600 dark:text-gray-300 mb-5 ml-[52px]">
          {message}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className={cn(
              'px-4 h-8 rounded text-xs font-semibold transition-colors',
              'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600',
              'text-gray-700 dark:text-gray-200',
              'disabled:opacity-50 disabled:cursor-not-allowed',
            )}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className={cn(
              'px-4 h-8 rounded text-xs font-semibold text-white transition-colors',
              'focus:outline-none focus:ring-2 focus:ring-offset-2',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'flex items-center gap-1.5',
              cfg.confirmCls,
            )}
          >
            {loading && <Spinner size="sm" className="text-white" />}
            {label}
          </button>
        </div>

        {/* Keyboard hint */}
        <p className="mt-3 text-center text-[10px] text-gray-400 dark:text-gray-500">
          Press <kbd className="px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-700 font-mono">Enter</kbd> to confirm
          &nbsp;·&nbsp;
          <kbd className="px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-700 font-mono">Esc</kbd> to cancel
        </p>
      </div>
    </div>
  );
};
